/* Settings — Appearance, Editor, Runtime config (admin), About. Mounted inside a modal <dialog>. */

import { esc, escAttr } from './state.js';
import { api } from './api.js';
import { isAdmin, getAuthMode } from './auth.js';
import {
  getTheme, setTheme, getEditorColors, setEditorColorsAll, resetEditorColors,
  EDITOR_COLOR_GROUPS,
} from './theme.js';

const SECTIONS = [
  { id: 'appearance', label: 'Appearance' },
  { id: 'editor',     label: 'Editor'     },
  { id: 'about',      label: 'About'      },
];

let _activeSection = 'appearance';
let _inited = false;

// esc() (textContent→innerHTML) does not escape quotes; use this for HTML attribute values.
/**
 * Sections visible to the current user. The admin-only "Runtime config" section mirrors the
 * Errors-panel gate: always shown in local/desktop mode, admin-gated (is_admin) in hosted mode.
 * The real boundary is the backend /api/admin/settings 403; this is UX only.
 */
function _visibleSections() {
  const sections = SECTIONS.slice();
  if (getAuthMode() !== 'hosted' || isAdmin()) {
    // Insert before "About" so config sits with the functional sections.
    sections.splice(sections.length - 1, 0, { id: 'runtime', label: 'Runtime config' });
  }
  return sections;
}

function _container() {
  return document.getElementById('settingsDialogBody');
}

function _dialog() {
  return document.getElementById('settingsDialog');
}

function initSettingsPanel() {
  if (_inited) return;
  const container = _container();
  const dialog = _dialog();
  if (!container || !dialog) return;

  container.innerHTML = `
    <div class="settings-panel">
      <div class="settings-header">
        <span class="settings-title">Settings</span>
        <button class="settings-close-btn" id="settingsCloseBtn" title="Close" aria-label="Close">&times;</button>
      </div>
      <div class="settings-body">
        <nav class="settings-nav" id="settingsNav"></nav>
        <section class="settings-content" id="settingsContent"></section>
      </div>
    </div>
  `;
  _renderNav(container);
  _renderContent(container);

  container.querySelector('#settingsCloseBtn')?.addEventListener('click', () => {
    closeSettingsDialog();
  });

  // Click outside the panel content (on the dialog backdrop) closes it
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) closeSettingsDialog();
  });
  // Native <dialog> Escape support fires `close` event automatically.

  _inited = true;
}

export function openSettingsDialog() {
  initSettingsPanel();
  const dialog = _dialog();
  if (!dialog) return;
  // Re-render nav + content so live values (theme, colors, admin gating) are current
  const container = _container();
  if (container) _renderNav(container);
  refreshSettingsPanel();
  if (!dialog.open) dialog.showModal();
}

function closeSettingsDialog() {
  const dialog = _dialog();
  if (dialog?.open) dialog.close();
}

function _renderNav(container) {
  const nav = container.querySelector('#settingsNav');
  if (!nav) return;
  nav.innerHTML = '';
  for (const sec of _visibleSections()) {
    const btn = document.createElement('button');
    btn.className = `settings-nav-item${_activeSection === sec.id ? ' active' : ''}`;
    btn.textContent = sec.label;
    btn.addEventListener('click', () => {
      _activeSection = sec.id;
      _renderNav(container);
      _renderContent(container);
    });
    nav.appendChild(btn);
  }
}

function _renderContent(container) {
  const content = container.querySelector('#settingsContent');
  if (!content) return;

  // Guard: if the active section is no longer visible (e.g. admin logged out), fall back.
  const visible = _visibleSections().map((s) => s.id);
  if (!visible.includes(_activeSection)) _activeSection = 'appearance';

  if (_activeSection === 'appearance') content.innerHTML = _renderAppearance();
  else if (_activeSection === 'editor') content.innerHTML = _renderEditor();
  else if (_activeSection === 'runtime') content.innerHTML = _renderRuntimeShell();
  else content.innerHTML = _renderAbout();

  _wireContent(container);
  if (_activeSection === 'runtime') _loadRuntime(container);
}

function _renderAppearance() {
  const theme = getTheme();
  const opt = (v, label) =>
    `<label class="settings-radio"><input type="radio" name="theme" value="${v}" ${theme === v ? 'checked' : ''}/><span>${label}</span></label>`;
  return `
    <h3 class="settings-section-title">Theme</h3>
    <div class="settings-radio-group" id="themeRadios">
      ${opt('light', 'Light')}
      ${opt('dark', 'Dark')}
      ${opt('auto', 'Auto (follow system)')}
    </div>
    <p class="settings-hint">Dark mode recolors the entire workspace including the code editor.</p>

    <h3 class="settings-section-title" style="margin-top: 18px">Layout</h3>
    <div class="settings-actions">
      <button class="btn btn-xs" id="btnResetLayout">Reset Layout</button>
    </div>
    <p class="settings-hint">Restore default panel positions and zone sizes.</p>
  `;
}

function _renderEditor() {
  const colors = getEditorColors();
  const rows = EDITOR_COLOR_GROUPS.map(g => `
    <tr>
      <td class="ec-label">${esc(g.label)}</td>
      <td class="ec-sample"><code>${esc(g.sample)}</code></td>
      <td class="ec-color">
        <input type="color" class="ec-picker" data-group="${g.id}" value="${colors[g.id]}"/>
      </td>
    </tr>
  `).join('');
  return `
    <h3 class="settings-section-title">Code Editor Colors</h3>
    <p class="settings-hint">JetBrains-style per-token color customization. Applies to Monaco syntax highlighting.</p>
    <table class="settings-editor-colors">
      <thead><tr><th>Group</th><th>Sample</th><th>Color</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="settings-actions">
      <button class="btn btn-xs" id="btnResetEditorColors">Reset to defaults</button>
    </div>
  `;
}

function _renderAbout() {
  return `
    <h3 class="settings-section-title">About</h3>
    <p class="settings-hint">AgentCPQ — SAP CPQ automation via HTTP.</p>
    <p class="settings-hint">Theme and editor preferences are stored locally per browser.</p>
  `;
}

// --- Runtime config (admin) ------------------------------------------------

function _renderRuntimeShell() {
  return `
    <h3 class="settings-section-title">Runtime config</h3>
    <p class="settings-hint">Override selected environment tunables without redeploying. Secrets and
      infrastructure settings are not editable here. Values marked <span class="rt-badge">restart</span>
      take effect on the next server restart.</p>
    <div class="rt-settings" id="rtSettingsBody"><div class="settings-hint">Loading…</div></div>
    <div id="rtHistory"></div>
  `;
}

async function _loadRuntime(container) {
  const body = container.querySelector('#rtSettingsBody');
  if (!body) return;
  let rows;
  try {
    const data = await api.settings();
    rows = (data && data.settings) || [];
  } catch (e) {
    body.innerHTML = `<div class="settings-hint rt-error">Could not load settings: ${esc(e.message || String(e))}</div>`;
    return;
  }
  if (!rows.length) {
    body.innerHTML = `<div class="settings-hint">No editable settings.</div>`;
    return;
  }
  const groups = {};
  for (const r of rows) (groups[r.category] = groups[r.category] || []).push(r);
  body.innerHTML = Object.entries(groups).map(([cat, items]) => `
    <div class="rt-group">
      <div class="rt-group-title">${esc(cat)}</div>
      ${items.map(_renderRow).join('')}
    </div>
  `).join('');
  _wireRuntime(body);
  _loadHistory();
}

async function _loadHistory() {
  const box = document.getElementById('rtHistory');
  if (!box) return;
  let rows;
  try {
    const data = await api.settingsAudit(50);
    rows = (data && data.entries) || [];
  } catch (e) {
    box.innerHTML = '';   // history is best-effort; no error surface
    return;
  }
  if (!rows.length) { box.innerHTML = ''; return; }
  box.innerHTML = `
    <h3 class="settings-section-title" style="margin-top:18px">Recent changes</h3>
    <div class="rt-history">${rows.map(_renderHistoryRow).join('')}</div>
  `;
  _wireHistory(box);
}

function _renderHistoryRow(e) {
  const who = e.by ? ` · ${esc(String(e.by))}` : '';
  const id = escAttr(String(e.id || ''));
  return `
    <div class="rt-hist-row" data-id="${id}">
      <code class="rt-key">${esc(String(e.key || ''))}</code>
      <span class="rt-hist-change">${esc(String(e.before))} &rarr; ${esc(String(e.after))}</span>
      <span class="rt-hist-meta">${esc(_fmtTs(e.ts))}${who}</span>
      <button class="btn btn-xs rt-revert" data-id="${id}" title="Set this setting back to its previous value">Revert</button>
      <span class="rt-hist-status" data-id="${id}"></span>
    </div>
  `;
}

function _wireHistory(box) {
  for (const btn of box.querySelectorAll('.rt-revert')) {
    btn.addEventListener('click', () => _doRevert(box, btn.dataset.id));
  }
}

async function _doRevert(box, id, typedConfirm) {
  const sel = `[data-id="${CSS.escape(id)}"]`;
  const btn = box.querySelector(`.rt-revert${sel}`);
  const status = box.querySelector(`.rt-hist-status${sel}`);
  if (btn) btn.disabled = true;
  if (status) { status.textContent = 'Reverting…'; status.className = 'rt-hist-status'; }
  try {
    await api.settingsRevert(id, typedConfirm);
    // Reload the whole runtime section so both the settings rows and history reflect the revert.
    const container = document.getElementById('settingsContent');
    if (container) _loadRuntime(container); else _loadHistory();
  } catch (e) {
    const msg = e.message || 'Error';
    // Reverting to a high-stakes value (e.g. PADDLE production) needs a typed confirmation.
    if (/confirm/i.test(msg) && typedConfirm === undefined) {
      const val = window.prompt(msg);
      if (val != null && val !== '') return _doRevert(box, id, val);
    }
    if (status) { status.textContent = msg; status.className = 'rt-hist-status rt-error'; }
    if (btn) btn.disabled = false;
  }
}

function _fmtTs(ts) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
}

function _renderRow(r) {
  const key = escAttr(r.key);
  const confirmChoices = Array.isArray(r.confirm_choices) ? r.confirm_choices : [];
  const badge = r.hot_reload
    ? ''
    : `<span class="rt-badge" title="Applies after a server restart">restart</span>`;
  let input;
  if (r.type === 'enum' && Array.isArray(r.choices)) {
    input = `<select class="rt-input" data-key="${key}" data-confirm="${escAttr(confirmChoices.join(','))}">
      ${r.choices.map(c =>
        `<option value="${escAttr(c)}" ${String(r.value) === String(c) ? 'selected' : ''}>${esc(c)}</option>`
      ).join('')}
    </select>`;
  } else {
    const step = r.type === 'float' ? ' step="any"' : '';
    const min = (r.minimum !== null && r.minimum !== undefined) ? ` min="${escAttr(r.minimum)}"` : '';
    input = `<input class="rt-input" type="number"${step}${min} data-key="${key}" value="${escAttr(r.value)}"/>`;
  }
  return `
    <div class="rt-row" data-key="${key}">
      <div class="rt-row-head">
        <code class="rt-key">${esc(r.key)}</code>${badge}
        <span class="rt-source rt-source-${escAttr(r.source)}">${esc(r.source)}</span>
      </div>
      <div class="rt-row-desc">${esc(r.description)}</div>
      <div class="rt-row-edit">
        ${input}
        <button class="btn btn-xs rt-save" data-key="${key}">Save</button>
        <span class="rt-status" data-key="${key}"></span>
      </div>
      <div class="rt-confirm" data-key="${key}" hidden>
        <input class="rt-confirm-input" data-key="${key}" placeholder="type value to confirm"/>
      </div>
    </div>
  `;
}

function _wireRuntime(body) {
  // Reveal the typed-confirm input when a select is switched to a confirm-gated value.
  for (const sel of body.querySelectorAll('select.rt-input')) {
    const choices = (sel.dataset.confirm || '').split(',').filter(Boolean);
    if (!choices.length) continue;
    sel.addEventListener('change', () => {
      const box = body.querySelector(`.rt-confirm[data-key="${CSS.escape(sel.dataset.key)}"]`);
      if (!box) return;
      const need = choices.includes(sel.value);
      box.hidden = !need;
      const ci = box.querySelector('.rt-confirm-input');
      if (need && ci) { ci.placeholder = `type "${sel.value}" to confirm`; ci.value = ''; ci.focus(); }
    });
  }
  for (const btn of body.querySelectorAll('.rt-save')) {
    btn.addEventListener('click', async () => {
      const key = btn.dataset.key;
      const sel = `[data-key="${CSS.escape(key)}"]`;
      const input = body.querySelector(`.rt-input${sel}`);
      const status = body.querySelector(`.rt-status${sel}`);
      if (!input) return;
      const value = input.value;
      // Typed-confirm gate for high-stakes values (e.g. PADDLE_ENVIRONMENT=production).
      const confirmChoices = (input.dataset.confirm || '').split(',').filter(Boolean);
      let typedConfirm;
      if (confirmChoices.includes(value)) {
        const box = body.querySelector(`.rt-confirm${sel}`);
        const ci = box && box.querySelector('.rt-confirm-input');
        if (box) box.hidden = false;
        typedConfirm = ci ? ci.value : '';
        if (typedConfirm !== value) {
          if (status) { status.textContent = `type "${value}" to confirm`; status.className = 'rt-status rt-error'; }
          if (ci) ci.focus();
          return;
        }
      }
      if (status) { status.textContent = 'Saving…'; status.className = 'rt-status'; }
      btn.disabled = true;
      try {
        const res = await api.settingsUpdate(key, value, typedConfirm);
        if (res && res.value !== undefined) input.value = res.value;
        if (status) { status.textContent = 'Saved'; status.className = 'rt-status rt-ok'; }
        const src = body.querySelector(`.rt-row${sel} .rt-source`);
        if (src) { src.textContent = 'override'; src.className = 'rt-source rt-source-override'; }
        const box = body.querySelector(`.rt-confirm${sel}`);
        if (box) box.hidden = true;
      } catch (e) {
        if (status) { status.textContent = e.message || 'Error'; status.className = 'rt-status rt-error'; }
      } finally {
        btn.disabled = false;
      }
    });
  }
}

function _wireContent(container) {
  const content = container.querySelector('#settingsContent');
  if (!content) return;

  const radios = content.querySelectorAll('#themeRadios input[name="theme"]');
  for (const r of radios) {
    r.addEventListener('change', (e) => {
      setTheme(e.target.value);
    });
  }

  const pickers = content.querySelectorAll('.ec-picker');
  for (const p of pickers) {
    p.addEventListener('change', (e) => {
      const group = e.target.dataset.group;
      const color = e.target.value;
      const current = getEditorColors();
      if (current[group] === color) return;
      setEditorColorsAll({ ...current, [group]: color });
    });
  }

  const resetBtn = content.querySelector('#btnResetEditorColors');
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      resetEditorColors();
      _renderContent(container);
    });
  }

  const resetLayoutBtn = content.querySelector('#btnResetLayout');
  if (resetLayoutBtn) {
    resetLayoutBtn.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('settings-reset-layout'));
    });
  }
}

/** Re-render content (kept for theme-change live preview). */
function refreshSettingsPanel() {
  if (!_inited) return;
  const container = _container();
  if (!container) return;
  _renderContent(container);
}
